import type { JSX } from 'react';
import { useState } from 'react';
import { Bot } from 'lucide-react';
import { applyTheme, otherTheme, storedTheme } from '@/lib/theme';
import type { Theme } from '@/lib/theme';
import { navigate } from './use-route';

/** "ONLINE", with the API's uptime on hover. */
export function OnlinePill({ uptime }: { uptime: string | undefined }): JSX.Element {
  return (
    <div
      title={uptime === undefined ? undefined : `Uptime ${uptime}`}
      className="text-mute bg-surf hidden items-center gap-2 rounded-full tab:flex border px-3 py-1.5 font-mono text-[11.5px] whitespace-nowrap"
    >
      <span className="bg-ok size-[7px] rounded-full shadow-[0_0_10px_var(--ok)]" />
      ONLINE
    </div>
  );
}

/** Phone only: the header's way to Agent settings, which has no tab (design `goSettings`). */
export function AgentButton({ active }: { active: boolean }): JSX.Element {
  return (
    <button
      type="button"
      title="Agent settings"
      aria-label="Agent settings"
      aria-current={active ? 'page' : undefined}
      onClick={() => navigate({ page: 'agent' })}
      className="grid size-9 place-items-center rounded-[10px] border tab:hidden"
      style={{
        background: active ? 'var(--accSoft)' : 'var(--surf)',
        color: active ? 'var(--acc)' : 'var(--mute)',
      }}
    >
      <Bot aria-hidden="true" size={18} strokeWidth={1.75} />
    </button>
  );
}

/** A glowing dot in dark mode, a crescent in light — the design's toggle. */
export function ThemeToggle(): JSX.Element {
  const [theme, setTheme] = useState<Theme>(storedTheme);
  const dark = theme === 'dark';
  return (
    <button
      type="button"
      title="Toggle theme"
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      onClick={() => {
        const next = otherTheme(theme);
        applyTheme(next);
        setTheme(next);
      }}
      className="bg-surf hover:bg-surf2 grid size-9 place-items-center rounded-[10px] border transition-colors"
    >
      <span
        className="size-3.5 rounded-full transition-all duration-300"
        style={{
          background: dark ? 'var(--acc)' : 'var(--ink)',
          boxShadow: dark ? '0 0 10px var(--acc)' : 'inset -4px -3px 0 0 var(--surf)',
        }}
      />
    </button>
  );
}
